import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './Button';

/** Must match the longer of modal-backdrop-out / modal-panel-out in tailwind.config.js */
const MODAL_CLOSE_MS = 300;

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md'
}) => {
  const titleId = useId();
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const [rendered, setRendered] = useState(isOpen);
  const [exiting, setExiting] = useState(false);
  // Apply aria-hidden only after focus has left the dialog (avoids Chrome a11y warning).
  const [a11yHidden, setA11yHidden] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setExiting(false);
      setRendered(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!rendered) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';

    const scrollRoots = typeof document !== 'undefined'
      ? document.querySelectorAll('[data-modal-scroll-lock]')
      : [];
    const scrollRootState = [];
    scrollRoots.forEach((el) => {
      scrollRootState.push({
        el,
        overflow: el.style.overflow,
        scrollTop: el.scrollTop,
      });
      el.style.overflow = 'hidden';
    });

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overscrollBehavior = prevHtmlOverscroll;
      body.style.overscrollBehavior = prevBodyOverscroll;
      scrollRootState.forEach(({ el, overflow, scrollTop }) => {
        el.style.overflow = overflow;
        el.scrollTop = scrollTop;
      });
    };
  }, [rendered]);

  useLayoutEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement;
      setA11yHidden(false);
      return undefined;
    }
    if (!rendered) {
      return undefined;
    }

    // Closing: restore focus to opener before aria-hidden, then hide from AT during exit.
    const container = containerRef.current;
    const active = document.activeElement;
    if (container && active instanceof Node && container.contains(active)) {
      const restore = previouslyFocusedRef.current;
      if (
        restore
        && typeof restore.focus === 'function'
        && document.contains(restore)
        && restore !== document.body
      ) {
        restore.focus({ preventScroll: true });
      } else if (typeof active.blur === 'function') {
        active.blur();
      }
    }
    setA11yHidden(true);

    setExiting(true);
    const t = setTimeout(() => {
      setRendered(false);
      setExiting(false);
      setA11yHidden(false);
    }, MODAL_CLOSE_MS);
    return () => clearTimeout(t);
  }, [isOpen, rendered]);

  if (!rendered) {
    return null;
  }

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
    full: 'max-w-7xl'
  };

  const backdropAnim = exiting
    ? 'animate-modal-backdrop-out motion-reduce:animate-none'
    : 'animate-modal-backdrop-in motion-reduce:animate-none';
  const panelAnim = exiting
    ? 'animate-modal-panel-out motion-reduce:animate-none'
    : 'animate-modal-panel-in motion-reduce:animate-none';

  /*
   * z-index: app chrome (header/sidebar ~40–50) < modal stack 112–125.
   * Full-viewport dim covers the entire UI including HelpDesk header; chrome stays visible through the tint.
   * Dialog panel stays above the dimmer for focus and clicks.
   */
  const modalTree = (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[112] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden sm:h-screen"
      aria-hidden={a11yHidden ? true : undefined}
    >
      <div
        className={`pointer-events-none absolute inset-0 h-full w-full bg-black/50 ${backdropAnim}`}
        aria-hidden="true"
      />

      <div
        className="relative z-[113] flex min-h-0 flex-1 cursor-default items-center justify-center overflow-y-auto overflow-x-hidden p-4 sm:p-6"
        onClick={onClose}
        role="presentation"
      >
        <div
          className={`pointer-events-auto relative z-[125] my-auto w-full max-h-[min(90vh,100dvh-2rem)] flex flex-col bg-white shadow-xl dark:bg-gray-800 rounded-2xl ${panelAnim} ${sizeClasses[size]}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: '#E5EFF3' }}>
            <h2 id={titleId} className="text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-1 hover:bg-[#F8FCFB] dark:hover:bg-gray-700 rounded-full"
            >
              <X size={20} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {children}
          </div>

          {footer && (
            <div className="flex-shrink-0 border-t px-4 py-3" style={{ borderColor: '#E5EFF3' }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(modalTree, document.body);
};

export { Modal };
